import { enableGridHeaderKeyboardScroll } from './workflow-grid-keyboard-scroll';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { AppApis } from '@app/core/APIs/app-apis';
import { UiButtonDirective } from '@template-ui/directives';
import { EzGridComponent } from '@template-ui/components/ez-grid/ez-grid.component';
import type { ColDef } from 'ag-grid-community';

interface EnrollmentCandidate {
  ordinal: number;
  personId: number;
  status: string;
  disposition: string | null;
  issueCode: string | null;
  enrollment: {
    hearingCycleId: number;
    workflowInstanceId: number;
    preparationOccurrenceId: number;
    publicReference: string;
  } | null;
}
interface EnrollmentPage {
  run: {
    runId: string;
    scheduleRunId: string;
    nominalDate: string;
    status: string;
    candidateCount: number;
    checkpointOrdinal: number;
    issueCode: string | null;
    counts: Record<string, number>;
  };
  nextAfterOrdinal: number | null;
  candidates: EnrollmentCandidate[];
}
@Component({
  selector: 'phms-workflow-enrollment-run-inspector',
  standalone: true,
  imports: [CommonModule, RouterLink, UiButtonDirective, EzGridComponent],
  template: `<section aria-label="Enrollment processing results">
    <h4>Enrollment results</h4>
    <p>
      Capture delivery and candidate processing are separate. Inspect with
      current program and person-read permissions.
    </p>
    <button uiButton type="button" (click)="load()" [disabled]="busy">
      Inspect / refresh enrollment results
    </button>
    <p *ngIf="busy" role="status">Loading enrollment results…</p>
    <p *ngIf="error" role="alert">{{ error }}</p>
    <ng-container *ngIf="page as result">
      <p role="status">
        {{ result.run.status }} · {{ result.run.checkpointOrdinal }} /
        {{ result.run.candidateCount }} candidates · Eligibility date
        {{ result.run.nominalDate }}
      </p>
      <p *ngIf="result.run.issueCode">{{ result.run.issueCode }}</p>
      <dl>
        <ng-container *ngFor="let count of result.run.counts | keyvalue"
          ><dt>{{ count.key }}</dt>
          <dd>{{ count.value }}</dd></ng-container
        >
      </dl>
      <ui-grid
        [rowData]="result.candidates"
        [columnDefs]="columns"
        [agGridOptions]="gridOptions"
        [showPanelHeader]="false"
        [showStandardFilterControls]="false"
        [showBlankRowsOnLastPage]="false"
        [autoHidePaginator]="true"
        containerClass="no-panel"
        width="fill"
      />
      <ul>
        <ng-container *ngFor="let candidate of result.candidates"
          ><li *ngIf="candidate.enrollment as enrollment">
            <a
              [routerLink]="[
                '/workflows',
                enrollment.workflowInstanceId,
              ]"
              >{{ enrollment.publicReference }} · Open workflow</a
            >
            <span>
              · Hearing cycle {{ enrollment.hearingCycleId }} · Workflow
              {{ enrollment.workflowInstanceId }}</span
            >
          </li></ng-container
        >
      </ul>
      <button
        uiButton
        *ngIf="result.nextAfterOrdinal !== null"
        type="button"
        (click)="load(result.nextAfterOrdinal!)"
        [disabled]="busy"
      >
        Next candidates
      </button>
    </ng-container>
  </section>`,
})
export class WorkflowEnrollmentRunInspectorComponent implements OnChanges {
  @Input({ required: true }) invocationId = '';
  private readonly http = inject(HttpClient);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly apis = inject(AppApis);
  private readonly destroy = inject(DestroyRef);
  page: EnrollmentPage | null = null;
  busy = false;
  error = '';
  private generation = 0;
  readonly columns: ColDef<EnrollmentCandidate>[] = [
    { field: 'ordinal', headerName: 'Candidate', width: 120 },
    { field: 'personId', headerName: 'Person', width: 120 },
    { field: 'status', headerName: 'Result', minWidth: 170, flex: 1 },
    { field: 'disposition', headerName: 'Eligibility', minWidth: 170, flex: 1 },
    { field: 'issueCode', headerName: 'Issue', minWidth: 180, flex: 1 },
  ];
  readonly gridOptions = {
    domLayout: 'autoHeight' as const,
    onGridReady: () =>
      enableGridHeaderKeyboardScroll(this.element.nativeElement),
    onGridSizeChanged: () =>
      enableGridHeaderKeyboardScroll(this.element.nativeElement),
    onFirstDataRendered: () =>
      enableGridHeaderKeyboardScroll(this.element.nativeElement),
    onRowDataUpdated: () =>
      enableGridHeaderKeyboardScroll(this.element.nativeElement),
  };
  ngOnChanges() {
    ++this.generation;
    this.page = null;
    this.error = '';
    this.busy = false;
  }
  load(afterOrdinal = 0) {
    const generation = ++this.generation;
    this.busy = true;
    this.error = '';
    this.page = null;
    this.http
      .get<EnrollmentPage>(
        `${this.apis.core.manual.getHearingEnrollmentRuns()}/${encodeURIComponent(this.invocationId)}`,
        {
          withCredentials: true,
          headers: new HttpHeaders({
            'skip-error-interceptor': 'yes',
            'skip-loading-interceptor': 'yes',
          }),
          params: new HttpParams()
            .set('afterOrdinal', afterOrdinal)
            .set('pageSize', 50),
        },
      )
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: (page) => {
          if (generation !== this.generation) return;
          this.busy = false;
          if (page.run.runId !== this.invocationId) {
            this.error =
              'Unexpected enrollment run. Refresh before inspecting.';
            return;
          }
          this.page = page;
        },
        error: (failure) => {
          if (generation !== this.generation) return;
          this.busy = false;
          this.error = [401, 403].includes(failure.status)
            ? 'Enrollment results require current scoped read permission.'
            : failure.status === 404
              ? 'Enrollment processing has not been captured yet. Refresh after delivery completes.'
              : 'Enrollment results are unavailable. Inspect capture delivery or refresh; no run was started by this read.';
        },
      });
  }
}
